import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
  clearPtoReportData,
  getEmployeeList,
  getMunisReportCsvData,
  getMunisReportData,
  setMunisReportCsvData,
} from '../../../store/ReportsDetailsSlice/actions';
import { useSelector } from 'react-redux';
import {
  selectMunisCsvReportData,
  selectMunisReportData,
} from '../../../store/ReportsDetailsSlice/selectors';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { GROUPBY } from '../../../constants/Reports';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  useMediaQuery,
} from '@mui/material';
import SelectRebuild from '../../../components/Inputs/SelectRebuild';
import { selectEmployeeData } from '../../../store/ReportsDetailsSlice/selectors';
import { Card } from './components/Card';
import { saveCsvToFile } from '../../../utils/saveCsvToFile';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import DataNotFound from '../../../components/Reports/DataNotFound';
import ReportsHeader from '../../../components/Reports/ReportsHeader';

const createParams = (param) =>
  param && param.length > 0 ? param.map((par) => par.id) : ['ALL'];

const MunisExportReport = () => {
  const dispatch = useDispatch();
  const { munisReportData, isMunisReportDataLoading } = useSelector(
    selectMunisReportData
  );
  const { isLoadingMunisCsvData, munisCsvReport } = useSelector(
    selectMunisCsvReportData
  );
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');
  const isTabletWidth = useMediaQuery('(max-width:899px)');

  const [params] = useSearchParams();
  const groupByParam = params.get('groupby') || GROUPBY[0].value;

  const { register, setValue, watch } = useForm({
    defaultValues: {
      emp_code: [],
      groupby: { id: 'Emp', name: 'Employee' },
    },
  });

  const { fromDate, toDate, render } = useDateRangeFilter();

  const { emp_code: employees, groupby } = watch();
  const employeeParam = useMemo(() => createParams(employees), [employees]);

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupby.id,
      sel_emp:
        employeeParam.length === 1 && employeeParam[0] === 'ALL'
          ? undefined
          : employeeParam,
    };
  }, [fromDate, toDate, groupby, employeeParam]);

  const handleDownloadReport = () => {
    const query = { ...param, csv: 1 };
    dispatch(getMunisReportCsvData(query));
  };

  const handleFindClick = () => dispatch(getMunisReportData(param));

  useEffect(() => {
    dispatch(getEmployeeList());
    dispatch(getMunisReportData(param));
    return () => dispatch(clearPtoReportData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (munisCsvReport?.data) {
      saveCsvToFile(munisCsvReport.data, munisCsvReport.filename);
      dispatch(setMunisReportCsvData({ data: null, filename: '' }));
    }
  }, [munisCsvReport, dispatch]);

  const rawFilterConfig = [
    {
      key: 'group_by',
      label: 'Group By',
      show: GROUPBY.length > 0,
      renderComponent: () => (
        <SelectRebuild
          options={GROUPBY.map((item) => ({
            id: item.value,
            name: item.title,
          }))}
          name="groupby"
          label="Group By"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: isTabletVerticalWidth ? 'start' : 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={handleDownloadReport}
            disabled={isLoadingMunisCsvData}
            text={'Export CSV'}
          />
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  const isNoData =
    munisReportData.outOfGrade.length === 0 &&
    munisReportData.timeOff.length === 0 &&
    munisReportData.overtime.length === 0;
  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Munis Export Report Details" />

      <Box className="mt-4 mb-4" sx={{ width: '100%' }}>
        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <FilterPanel
            filters={filterConfig}
            columns={isTabletVerticalWidth ? 1 : 4}
          />
        </Grid>

        <Grid container sx={{ width: '100%' }}>
          <Grid
            item
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: isTabletWidth ? 'column' : 'row',
                gap: 2,
                width: '100%',
                mt: 'auto',
              }}
            >
              {render}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {isMunisReportDataLoading ? (
        <Box
          sx={{ minHeight: '30px' }}
          className="w-10 h-10 d-flex justify-content-center align-items-center"
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
          {isNoData ? (
            <DataNotFound />
          ) : (
            <>
              {' '}
              <Card
                data={munisReportData.overtime}
                title={'Overtime'}
                groupBy={groupByParam}
              />
              <Card
                data={munisReportData.timeOff}
                title={'Time off'}
                groupBy={groupByParam}
              />
              <Card
                data={munisReportData.outOfGrade}
                title={'Out of Grade'}
                groupBy={groupByParam}
              />
            </>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default MunisExportReport;
